import { addVariationsToProducts } from '../woocommerce/loop-products'

/**
 * Load More functionality with Infinite Scroll and History State Preservation
 * 
 * Features:
 * - Search load more with Relevanssi integration
 * - Regular post/archive load more
 * - Multiple instances support via data-instance-id
 * - Infinite scroll via IntersectionObserver
 * - State preservation: saves loaded content and scroll position per instance
 * - Restores state on back-navigation or revisit
 */

// Track loading state per instance to prevent multiple simultaneous requests
const loadingStates = new Map()

// Storage key prefix
const STORAGE_KEY_PREFIX = 'list_state_'

// ============================================================================
// Instance & Container Management
// ============================================================================

/**
 * Get instance ID from button
 */
const getInstanceId = (button) => {
    return button?.dataset?.instanceId || null
}

/**
 * Generate a state key for storage based on button data
 */
const getStateKey = (button) => {
    const instanceId = getInstanceId(button)
    
    // If we have a search query, use that as part of the key
    if (button?.dataset?.searchQuery) {
        return `search:${button.dataset.searchQuery}`
    }
    
    // If we have a category ID, use post type + cat
    if (button?.dataset?.catId) {
        const postType = button.dataset.postType || 'post'
        return `archive:${postType}:${button.dataset.catId}`
    }

    // Kategoriurvalet ingår i nyckeln: ändras urvalet i admin ska ett sparat
    // state inte återställa den gamla listan vid bakåtnavigering.
    if (button?.dataset?.categories) {
        const instancePart = instanceId || button.dataset.postType || 'post'
        return `instance:${instancePart}:${button.dataset.categories}:${window.location.pathname}`
    }
    
    // If we have an instance ID (for page components), use that + path
    if (instanceId) {
        return `instance:${instanceId}:${window.location.pathname}`
    }
    
    // Fallback to post type + path
    const postType = button?.dataset?.postType || 'post'
    return `listing:${postType}:${window.location.pathname}`
}

/**
 * Get storage key for sessionStorage
 */
const getStorageKey = (stateKey) => {
    return STORAGE_KEY_PREFIX + stateKey
}

/**
 * Get the post container for a button
 * Supports instance-id scoping for multiple components
 */
const getPostContainer = (button) => {
    const instanceId = getInstanceId(button)
    
    if (instanceId) {
        // Find container with matching instance-id
        const container = document.querySelector(`[data-instance-id="${instanceId}"]`)
        if (container) {
            // The container itself might be the one with instance-id, look for post-listing inside
            const postListing = container.querySelector('.post-listing')
            return postListing || container
        }
    }
    
    // Fallback: find closest container and then post-listing
    return button?.closest('.container')?.querySelector('.post-listing')
}

/**
 * Check if instance is currently loading
 */
const isInstanceLoading = (button) => {
    const instanceId = getInstanceId(button) || 'global'
    return loadingStates.get(instanceId) || false
}

/**
 * Set loading state for instance
 */
const setInstanceLoading = (button, loading) => {
    const instanceId = getInstanceId(button) || 'global'
    loadingStates.set(instanceId, loading)
}

// ============================================================================
// State Preservation (sessionStorage)
// ============================================================================

/**
 * Save current state to sessionStorage
 */
const saveState = (button, container) => {
    const stateKey = getStateKey(button)
    if (!stateKey || !container) return

    const state = {
        html: container.innerHTML,
        paged: button?.dataset.paged || '1',
        maxPages: button?.dataset.maxPages || '',
        scrollY: window.scrollY,
        timestamp: Date.now(),
    }

    try {
        sessionStorage.setItem(getStorageKey(stateKey), JSON.stringify(state))
    } catch (e) {
        console.warn('Could not save list state:', e)
    }
}

/**
 * Restore state from sessionStorage
 */
const restoreState = (button, container) => {
    const stateKey = getStateKey(button)
    if (!stateKey || !container) return false

    try {
        const stored = sessionStorage.getItem(getStorageKey(stateKey))
        if (!stored) return false

        const state = JSON.parse(stored)

        // Check if state is not too old (30 minutes)
        if (Date.now() - state.timestamp > 30 * 60 * 1000) {
            sessionStorage.removeItem(getStorageKey(stateKey))
            return false
        }

        // Restore HTML content
        container.innerHTML = state.html

        // Restore button state
        if (button) {
            button.dataset.paged = state.paged
            if (state.maxPages) {
                button.dataset.maxPages = state.maxPages
            }
        }

        // Restore scroll position after a small delay (let DOM settle)
        requestAnimationFrame(() => {
            window.scrollTo(0, state.scrollY)
        })

        // Re-initialize WooCommerce variations
        addVariationsToProducts()

        return true
    } catch (e) {
        console.warn('Could not restore list state:', e)
        return false
    }
}

/**
 * Clear old states from sessionStorage (cleanup)
 */
const clearOldStates = () => {
    try {
        const keysToRemove = []
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i)
            if (key?.startsWith(STORAGE_KEY_PREFIX)) {
                const stored = sessionStorage.getItem(key)
                if (stored) {
                    const state = JSON.parse(stored)
                    if (Date.now() - state.timestamp > 30 * 60 * 1000) {
                        keysToRemove.push(key)
                    }
                }
            }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key))
    } catch (e) {
        // Fail silently
    }
}

/**
 * Setup state preservation for all load more instances
 */
const setupStatePreservation = () => {
    const loadMoreButtons = document.querySelectorAll('.load-more')
    
    loadMoreButtons.forEach((button) => {
        const container = getPostContainer(button)
        if (!container) return
        
        // Save state when clicking on any link inside the container
        container.addEventListener('click', (e) => {
            const link = e.target.closest('a')
            if (link && !link.closest('.load-more')) {
                saveState(button, container)
            }
        })
    })

    // Save all states before page unload
    window.addEventListener('pagehide', () => {
        loadMoreButtons.forEach((button) => {
            const container = getPostContainer(button)
            if (container) {
                saveState(button, container)
            }
        })
    })
}

/**
 * Try to restore state for all instances on page load
 */
const tryRestoreStates = () => {
    const loadMoreButtons = document.querySelectorAll('.load-more')
    
    loadMoreButtons.forEach((button) => {
        const container = getPostContainer(button)
        if (!container) return
        
        const restored = restoreState(button, container)
        
        if (restored) {
            // Hide button if we've loaded all pages
            const maxPages = parseInt(button.dataset.maxPages || '999', 10)
            const currentPage = parseInt(button.dataset.paged || '1', 10)
            if (currentPage >= maxPages) {
                button.style.display = 'none'
            }
        }
    })
}

// ============================================================================
// Core Load More Functions
// ============================================================================

/**
 * Get the load more button from click target
 */
const getLoadMoreButton = (target) => target?.closest('.load-more')

/**
 * Set button visual state
 */
const setButtonState = (button, { loading, disabled, text, hide }) => {
    if (typeof loading !== 'undefined') {
        button.classList.toggle('loading', loading)
        setInstanceLoading(button, loading)
    }

    if (typeof disabled !== 'undefined') {
        if (disabled) {
            button.setAttribute('disabled', 'disabled')
        } else {
            button.removeAttribute('disabled')
        }
    }

    if (typeof text !== 'undefined') {
        const label = button.querySelector('.load-more-text')
        if (label) {
            label.textContent = text
        }
    }

    if (typeof hide !== 'undefined') {
        button.style.display = hide ? 'none' : ''
    }
}

/**
 * Append posts to container
 */
const appendPosts = (container, posts = []) => {
    posts.forEach((post) => {
        container.insertAdjacentHTML('beforeend', post)
    })
}

/**
 * Check if this is a search load more button
 */
const isSearchLoadMore = (button) => {
    return button.dataset.searchQuery && button.dataset.searchQuery.length > 0
}

// ============================================================================
// Search Load More (Relevanssi)
// ============================================================================

/**
 * Build request data for search load more
 */
const buildSearchRequestData = (button) => {
    const data = new FormData()
    const { dataset } = button

    const currentPage = parseInt(dataset.paged || '1', 10)
    const nextPage = currentPage + 1

    data.append('action', 'search_load_more')
    data.append('security', liGlobal.loadMore)
    data.append('search_query', dataset.searchQuery)
    data.append('paged', nextPage)

    if (dataset.postsPerPage) {
        data.append('posts_per_page', dataset.postsPerPage)
    }

    if (dataset.postType) {
        data.append('post_type', dataset.postType)
    }

    return { data, nextPage }
}

/**
 * Delad POST-helper för load-more-anrop (sök + vanlig). Returnerar payload.data
 * eller kastar ett fel med ett kontext-anpassat nätverksfelmeddelande.
 */
const postLoadMore = async (formData, networkErrorMessage) => {
    const response = await fetch(liGlobal.ajaxUrl, {
        method: 'POST',
        body: formData,
    })

    if (!response.ok) {
        throw new Error(networkErrorMessage)
    }

    const payload = await response.json()

    if (!payload.success) {
        throw new Error(payload.data?.message || 'Servern returnerade ett fel')
    }

    return payload.data
}

/**
 * Handle search load more request
 */
const requestSearchLoadMore = (formData) => postLoadMore(formData, 'Nätverksfel vid sökning')

/**
 * Handle successful search load more
 */
const handleSearchLoadMoreSuccess = (button, container, data, nextPage) => {
    appendPosts(container, data.posts)

    button.dataset.paged = nextPage.toString()
    button.dataset.maxPages = data.max_num_pages?.toString() || ''

    if (data.no_more_posts) {
        setButtonState(button, {
            loading: false,
            disabled: true,
            hide: true,
        })
    } else {
        setButtonState(button, {
            loading: false,
            disabled: false,
        })
    }

    if (data.posts && data.posts.length > 0) {
        addVariationsToProducts()
    }

    // Save state after loading more
    saveState(button, container)
}

/**
 * Load search posts (can be triggered by click or intersection)
 */
const triggerSearchLoadMore = async (button) => {
    if (isInstanceLoading(button) || button.disabled) {
        return
    }

    const postContainer = getPostContainer(button)
    if (!postContainer) {
        return
    }

    setButtonState(button, { loading: true })

    const { data: formData, nextPage } = buildSearchRequestData(button)

    try {
        const data = await requestSearchLoadMore(formData)

        if (data.no_more_posts && !data.posts?.length) {
            setButtonState(button, {
                loading: false,
                disabled: true,
                hide: true,
            })
            return
        }

        handleSearchLoadMoreSuccess(button, postContainer, data, nextPage)
    } catch (error) {
        console.error('Kunde inte ladda fler sökresultat', error)
        setButtonState(button, { loading: false, disabled: false })
    }
}

/**
 * Main search load more handler (for click events)
 */
const loadSearchPosts = async (event) => {
    event.preventDefault()

    const loadMoreBtn = getLoadMoreButton(event.target)
    if (!loadMoreBtn) {
        return
    }

    await triggerSearchLoadMore(loadMoreBtn)
}

// ============================================================================
// Regular Load More (for non-search pages)
// ============================================================================

/**
 * Collect IDs of already loaded posts within a specific container
 */
const collectExcludedPostIds = (container) => {
    if (!container) {
        return [...document.querySelectorAll('[data-post-id]')].map((post) => post.dataset.postId).filter(Boolean)
    }
    return [...container.querySelectorAll('[data-post-id]')].map((post) => post.dataset.postId).filter(Boolean)
}

/**
 * Build request data for regular load more
 */
const buildRequestData = (button, container) => {
    const data = new FormData()
    const { dataset } = button
    
    const currentPage = parseInt(dataset.paged || '1', 10)
    const nextPage = currentPage + 1

    if (dataset.catId) {
        data.append('cat_id', dataset.catId)
    }

    // Komponentens fasta kategoriurval (ACF post_listing_categories). Utan detta
    // bygger servern en obegränsad fråga och sida 2 innehåller inlägg från
    // kategorier som aldrig valdes i admin.
    if (dataset.categories) {
        data.append('categories', dataset.categories)
    }

    if (dataset.productCategory) {
        data.append('product_id', dataset.productCategory)
    }

    if (dataset.productBrand) {
        data.append('product_brand', dataset.productBrand)
    }

    const excludedIds = collectExcludedPostIds(container)
    if (excludedIds.length) {
        data.append('exclude', excludedIds.join(','))
    }

    if (dataset.postType) {
        data.append('post_type', dataset.postType)
    }

    data.append('card_class', dataset.cardClass || '')
    data.append('card_body', dataset.cardBody || '')
    data.append('action', 'load_more')
    data.append('security', liGlobal.loadMore)
    data.append('paged', nextPage)

    return { data, nextPage }
}

/**
 * Request regular load more posts
 */
const requestLoadMorePosts = (formData) => postLoadMore(formData, 'Nätverksfel vid Ladda fler-förfrågan')

/**
 * Handle successful regular load more
 */
const handleLoadMoreSuccess = (button, container, data, nextPage) => {
    appendPosts(container, data.posts)
    
    button.dataset.paged = nextPage.toString()
    button.dataset.maxPages = data.max_num_pages?.toString() || ''

    if (data.no_more_posts) {
        setButtonState(button, {
            loading: false,
            disabled: true,
            hide: true,
        })
    } else {
        setButtonState(button, {
            loading: false,
            disabled: false,
        })
    }

    if (data.post_type === 'product') {
        addVariationsToProducts()
    }

    // Save state after loading more
    saveState(button, container)
}

/**
 * Handle load more error
 */
const handleLoadMoreError = (button, error) => {
    console.error('Kunde inte ladda fler inlägg', error)
    setButtonState(button, { loading: false, disabled: false })
}

/**
 * Trigger regular load more (can be triggered by click or intersection)
 */
const triggerLoadMore = async (button) => {
    if (isInstanceLoading(button) || button.disabled) {
        return
    }

    const postContainer = getPostContainer(button)
    if (!postContainer) {
        return
    }

    setButtonState(button, { loading: true })

    const { data: formData, nextPage } = buildRequestData(button, postContainer)

    try {
        const data = await requestLoadMorePosts(formData)

        if (data.no_more_posts && !data.posts?.length) {
            setButtonState(button, {
                loading: false,
                disabled: true,
                hide: true,
            })
            return
        }

        handleLoadMoreSuccess(button, postContainer, data, nextPage)
    } catch (error) {
        handleLoadMoreError(button, error)
    }
}

/**
 * Main regular load more handler (for click events)
 */
export const loadPosts = async (event) => {
    event.preventDefault()

    const loadMoreBtn = getLoadMoreButton(event.target)
    if (!loadMoreBtn) {
        return
    }

    await triggerLoadMore(loadMoreBtn)
}

// ============================================================================
// Infinite Scroll with IntersectionObserver
// ============================================================================

/**
 * Initialize infinite scroll for all load more buttons
 */
const initInfiniteScroll = () => {
    const loadMoreButtons = document.querySelectorAll('.load-more[data-infinite-scroll="true"]')

    if (!loadMoreButtons.length) {
        return
    }

    const observerOptions = {
        root: null,
        rootMargin: '400px',
        threshold: 0,
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const button = entry.target

                if (button.disabled || isInstanceLoading(button)) {
                    if (button.disabled) {
                        observer.unobserve(button)
                    }
                    return
                }

                if (isSearchLoadMore(button)) {
                    triggerSearchLoadMore(button)
                } else {
                    triggerLoadMore(button)
                }
            }
        })
    }, observerOptions)

    loadMoreButtons.forEach((button) => {
        observer.observe(button)
    })
}

// ============================================================================
// Event Listeners & Initialization
// ============================================================================

// Click handler for manual load more
document.addEventListener('click', (event) => {
    const loadMoreBtn = event.target.closest('.load-more')

    if (loadMoreBtn) {
        if (isSearchLoadMore(loadMoreBtn)) {
            loadSearchPosts(event)
        } else {
            loadPosts(event)
        }
    }
})

// Initialize when DOM is ready
const init = () => {
    // Clean up old states
    clearOldStates()

    // Try to restore state for all instances
    tryRestoreStates()

    // Setup state preservation for future navigation
    setupStatePreservation()

    // Initialize infinite scroll
    initInfiniteScroll()
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
} else {
    init()
}

// Handle pageshow event for bfcache restoration
window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        tryRestoreStates()
    }
})
